import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { cn } from '../../../../utils/utils';

@Component({
  selector: 'app-search-input',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchInputComponent),
      multi: true,
    },
  ],
  templateUrl: './search-input.component.html',
  styleUrl: './search-input.component.css',
})
export class SearchInputComponent implements OnDestroy {
  @Input() placeholder: string = '';
  /** Accessible name when the placeholder alone is vague ("Search recipients"). */
  @Input() label: string = '';
  @Input() className: string = '';
  /** Debounce in ms applied to valueChange/onChange emissions. 0 = no debounce. */
  @Input() debounce: number = 0;
  @Output() valueChange = new EventEmitter<string>();

  merge = cn;

  value: string = '';
  isFocused: boolean = false;

  public onChange: (value: string) => void = () => {};
  public onTouched: () => void = () => {};

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  private emit(next: string, immediate = false) {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (immediate || !this.debounce || this.debounce <= 0) {
      this.onChange(next);
      this.valueChange.emit(next);
      return;
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.onChange(next);
      this.valueChange.emit(next);
    }, this.debounce);
  }

  onInputChange(event: Event) {
    const next = (event.target as HTMLInputElement).value;
    this.value = next;
    this.emit(next);
  }

  clear() {
    this.value = '';
    this.emit('', true); // clear is intentional — bypass debounce
  }

  ngOnDestroy(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  writeValue(value: string | null): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}
}
